import _cloneDeep from 'lodash/cloneDeep';
import _map from 'lodash/map';
import { func } from 'prop-types';
import React, { createRef } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import templateReplace from 'es6-template-strings';

import Typography from 'ravenjs/lib/Typography';
import { scrollTo } from 'ravenjs/utils/scroll';
import Col from 'ravenjs/lib/Col';
import Button from 'ravenjs/lib/Button';
import { get } from 'ravenjs/utils/lodash';
import Row from 'ravenjs/lib/Row';
import { renderCheck } from 'ravenjs/utils/viewport';

import Questionnaire from 'components/Questionnaire';
import WizardBreadcrumbs from 'components/WizardBreadcrumbs';
import BROWSER_TITLES from 'constants/browserTitles';
import { EVENTS } from 'constants/events';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { HISTORY, MATCH } from 'constants/proptypes';
import { actions as brandingActions } from 'modules/branding';
import { actions as hrFitnessTestActions } from 'modules/hrFitnessTest';
import { actions as pendoActions } from 'modules/pendo';
import { actions as uiActions } from 'modules/ui';
import { resetBrowserTitle, setBrowserTitle } from 'utils/browserTitles';
import { extractErrorMessage } from 'utils/errors';

import {
    AssessmentToolStyled,
    BoxActionsStyled,
    ContentStyled,
    MobileAssessmentToolContentStyled,
    MobileAssessmentToolStyled,
    SideBarStyled,
} from './AssessmentToolStyled';

class AssessmentTool extends React.Component {
    hrFitnessTestViewRef = createRef();

    containerFaqViewRef = createRef();

    constructor(props) {
        super(props);

        this.state = {
            accessCode: null,
            categories: null,
            currentCategory: null,
            currentCategoryName: '',
            disableNext: true,
            disablePrevious: true,
            isLoading: false,
            missedQuestions: [],
            questionnaire: null,
            questionsByCategory: [],
            selectedAnswers: {},
            disableSendTest: true,
            displaySendTest: false,
            sizeQuestionsArray: 0,
            statusByCategory: [],
            partnerId: null,
            questionsIdIndexPair: {},
        };
    }

    componentDidMount() {
        const { match } = this.props;
        if (match.path === '/public/prospect/assessment-tool') {
            setBrowserTitle(
                BROWSER_TITLES.PUBLIC.PROSPECT.ASSESSMENT_TOOL,
                BROWSER_TITLES.PUBLIC.PROSPECT.SECTION
            );
        }

        this.fetchInitialData();
    }

    componentWillUnmount = () => {
        resetBrowserTitle();
    };

    fetchInitialData = async () => {
        const { fetchProspectQuestionnaire, history, isLoading } = this.props;
        isLoading(true, MESSAGES.LOADING.DEFAULT);
        const queryParams = get(history.location, 'search', '');
        const queryParamsParse = queryString.parse(queryParams);
        const accessCode = get(queryParamsParse, 'accessCode', null);
        const partnerId = get(queryParamsParse, 'partnerId', null);

        try {
            const response = await fetchProspectQuestionnaire({ accessCode });
            const questionnaire = get(response, 'questions', {});
            const categories = Object.keys(questionnaire);
            const questionsByCategory = [];
            const statusByCategory = [];
            const missedQuestions = [];
            statusByCategory[categories[0]] = 'current';
            let sizeQuestionsArray = 0;
            const questionsIdIndexPair = {};
            categories.forEach(function(category) {
                questionsByCategory[category] = _map(questionnaire[category], 'id');
                missedQuestions[category] = [];
                sizeQuestionsArray += questionnaire[category].length;
                // Preparing question id and index collections to determine the question index based on ids while tracking pendo events.
                questionsByCategory[category].forEach(id => {
                    questionsIdIndexPair[id] = Object.keys(questionsIdIndexPair).length;
                });
            });

            this.setState({
                accessCode,
                categories,
                currentCategory: questionnaire[categories[0]],
                currentCategoryName: categories[0],
                isLoading: false,
                missedQuestions,
                questionnaire,
                questionsByCategory,
                sizeQuestionsArray,
                statusByCategory,
                partnerId,
                questionsIdIndexPair,
            });
        } catch (error) {
            history.replace('/public/prospect/assessment-tool-invalid');
        } finally {
            isLoading(false);
        }
    };

    handleNextNavigate = () => {
        const { categories, currentCategoryName } = this.state;
        let currentPosition = categories.indexOf(currentCategoryName);
        currentPosition =
            currentPosition < categories.length - 1 ? currentPosition + 1 : currentPosition;

        this.setState(
            () => ({
                displaySendTest: currentPosition === categories.length - 1,
                disableNext: currentPosition === categories.length - 1,
                disablePrevious: currentPosition === 0,
            }),
            () => {
                this.onChangeCategory(categories[currentPosition]);
            }
        );
    };

    handlePreviousNavigate = () => {
        const { categories, currentCategoryName } = this.state;
        let currentPosition = categories.indexOf(currentCategoryName);
        currentPosition = Math.max(0, currentPosition - 1);
        this.setState(
            () => ({
                displaySendTest: currentPosition === categories.length - 1,
                disableNext: currentPosition === categories.length - 1,
                disablePrevious: currentPosition === 0,
            }),
            () => {
                this.onChangeCategory(categories[currentPosition]);
            }
        );
    };

    createQuestionnairePayload = (questionsIds, selectedAnswers) => {
        const payload = [];
        questionsIds.forEach(id => {
            payload.push({
                question_id: id,
                answer_ids: selectedAnswers[id],
            });
        });
        return payload;
    };

    handleSendTest = async () => {
        const { accessCode, selectedAnswers } = this.state;
        const { closeModal, history, isLoading, openModal, prospectSendResponses } = this.props;
        const questionsIds = Object.keys(selectedAnswers);
        const data = {
            access_code: accessCode,
            responses: this.createQuestionnairePayload(questionsIds, selectedAnswers),
        };

        isLoading(true, MESSAGES.LOADING.DEFAULT);
        try {
            const response = await prospectSendResponses({
                data,
            });

            if (response.status === 200) {
                history.replace('/public/prospect/assessment-tool-success');
            }
        } catch (error) {
            openModal(MODALS.CONFIRM, {
                title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                description: extractErrorMessage(error),
                actions: [
                    {
                        text: MESSAGES.MODAL.CONFIRM.ACTIONS.OK,
                        color: 'primary',
                        onClick() {
                            closeModal(MODALS.CONFIRM);
                        },
                    },
                ],
            });
        } finally {
            isLoading(false);
        }
    };

    showErrors = section => {
        const {
            currentCategoryName,
            missedQuestions,
            questionsByCategory,
            selectedAnswers,
            statusByCategory,
        } = this.state;
        const questions = questionsByCategory[currentCategoryName];
        const answeredQuestions = [];
        questions.forEach(question => {
            if (Object.keys(selectedAnswers).includes(question)) {
                answeredQuestions.push(question);
            }
        });
        missedQuestions[currentCategoryName] = questions.filter(
            question => !answeredQuestions.includes(question)
        );
        if (currentCategoryName === section) {
            statusByCategory[currentCategoryName] = 'current';
        } else if (questions.every(question => Object.keys(selectedAnswers).includes(question))) {
            statusByCategory[currentCategoryName] = 'completed';
        } else {
            statusByCategory[currentCategoryName] = 'error';
        }

        this.setState({
            missedQuestions,
            statusByCategory,
        });
    };

    onChangeCategory = category => {
        const {
            categories,
            currentCategoryName,
            questionnaire,
            statusByCategory,
            selectedAnswers,
        } = this.state;
        const currentPosition = categories.indexOf(category);
        statusByCategory[category] = 'current';
        const selectedValues = _cloneDeep(selectedAnswers);
        const SelectedCategory = _cloneDeep(questionnaire[category]);

        if (category !== currentCategoryName) {
            this.showErrors();
        }

        this.setState(
            () => ({
                currentCategory: questionnaire[category],
                currentCategoryName: category,
                disableNext:
                    SelectedCategory.filter(
                        category => Object.keys(selectedValues).indexOf(category.id) !== -1
                    ).length !== SelectedCategory.length,
                disablePrevious: currentPosition === 0,
                displaySendTest: currentPosition === categories.length - 1,
                statusByCategory,
            }),
            () => {
                if (window.innerHeight <= 800) {
                    scrollTo(this.hrFitnessTestViewRef.current, {
                        containerOffset: 0,
                    });
                }
                this.containerFaqViewRef.current.scrollIntoView();
            }
        );
    };

    onChangeOption = (id, questionId, type) => {
        const {
            currentCategoryName,
            currentCategory,
            missedQuestions,
            selectedAnswers,
            sizeQuestionsArray,
            partnerId,
            questionsIdIndexPair,
        } = this.state;
        const { sendPublicPendoEvent } = this.props;
        let selectedValues = _cloneDeep(selectedAnswers);
        const SelectedCategory = _cloneDeep(currentCategory);
        let questionArray = type === 'Multiple Choice' ? [] : get(selectedValues, questionId, []);
        const position = questionArray.indexOf(id);
        delete selectedValues[questionId];
        let disableSendTest = true;
        const questionIndex = questionsIdIndexPair[questionId];

        if (position === -1) {
            questionArray.push(id);
        } else {
            questionArray = questionArray.filter(selectedValue => selectedValue !== id);
        }
        if (questionArray.length) {
            selectedValues = { ...selectedValues, [questionId]: questionArray };
        }

        if (sizeQuestionsArray === Object.keys(selectedValues).length) {
            disableSendTest = false;
        }

        this.setState(
            () => ({
                disableSendTest,
                selectedAnswers: selectedValues,
                disableNext:
                    SelectedCategory.filter(
                        category => Object.keys(selectedValues).indexOf(category.id) !== -1
                    ).length !== SelectedCategory.length,
            }),
            () => {
                if (missedQuestions[currentCategoryName].length > 0) {
                    this.showErrors(currentCategoryName);
                }
            }
        );

        sendPublicPendoEvent({
            userId: 'prospect',
            partnerId,
            event: templateReplace(EVENTS.ASSESSMENT_TOOL.EVENT, {
                questionIndex: questionIndex + 1,
            }),
            eventProperties: {
                description: templateReplace(EVENTS.ASSESSMENT_TOOL.EVENT_PROPERTIES.DESCRIPTION, {
                    questionIndex: questionIndex + 1,
                }),
            },
        });
    };

    renderButtons = () => {
        const { disableNext, disablePrevious, disableSendTest, displaySendTest } = this.state;
        const button = displaySendTest ? (
            <Button
                disabled={disableSendTest}
                color={disableSendTest ? 'lightOutline' : 'primary'}
                onClick={this.handleSendTest}
            >
                {MESSAGES.PROSPECT_ASSESSMENT_TOOL.BUTTONS.SUBMIT_TEST}
            </Button>
        ) : (
            <Button
                disabled={disableNext}
                border
                color={disableNext ? 'lightOutline' : 'primary'}
                onClick={this.handleNextNavigate}
            >
                {MESSAGES.PROSPECT_ASSESSMENT_TOOL.BUTTONS.NEXT}
            </Button>
        );

        return (
            <>
                <Button
                    disabled={disablePrevious}
                    border
                    color={disablePrevious ? 'lightOutline' : 'primary'}
                    onClick={this.handlePreviousNavigate}
                >
                    {MESSAGES.PROSPECT_ASSESSMENT_TOOL.BUTTONS.PREVIOUS}
                </Button>
                {button}
            </>
        );
    };

    render() {
        const isMobile = renderCheck('md', 'less');
        const {
            categories,
            currentCategoryName,
            isLoading,
            statusByCategory,
            currentCategory,
            missedQuestions,
            selectedAnswers,
        } = this.state;

        if (isLoading || !categories) {
            return null;
        }

        return isMobile ? (
            <MobileAssessmentToolStyled>
                <MobileAssessmentToolContentStyled height="40px">
                    <Typography
                        fontSize="1.875rem"
                        fontFamily="Favorit"
                        gutterTop="0"
                        gutterBottom="0"
                    >
                        {MESSAGES.PROSPECT_ASSESSMENT_TOOL.TITLE}
                    </Typography>
                </MobileAssessmentToolContentStyled>
                <MobileAssessmentToolContentStyled height="145px">
                    <WizardBreadcrumbs
                        currentStepName={currentCategoryName}
                        steps={categories}
                        statusByStep={statusByCategory}
                        onChangeStep={this.onChangeCategory}
                        isMobile
                    />
                </MobileAssessmentToolContentStyled>
                <MobileAssessmentToolContentStyled height="470px">
                    <Questionnaire
                        currentCategory={currentCategory}
                        currentCategoryName={currentCategoryName}
                        missedQuestions={missedQuestions}
                        selectedAnswers={selectedAnswers}
                        onChangeOption={this.onChangeOption}
                        containerRef={this.containerFaqViewRef}
                    />
                </MobileAssessmentToolContentStyled>
                <BoxActionsStyled>{this.renderButtons()}</BoxActionsStyled>
            </MobileAssessmentToolStyled>
        ) : (
            <AssessmentToolStyled>
                <SideBarStyled>
                    <WizardBreadcrumbs
                        currentStepName={currentCategoryName}
                        steps={categories}
                        statusByStep={statusByCategory}
                        onChangeStep={this.onChangeCategory}
                    />
                </SideBarStyled>
                <ContentStyled>
                    <Row ref={this.hrFitnessTestViewRef}>
                        <Col size={{ sm: 12, lg: 8 }}>
                            <Typography fontSize="3rem" gutterTop="0">
                                {MESSAGES.PROSPECT_ASSESSMENT_TOOL.TITLE}
                            </Typography>
                            <Questionnaire
                                currentCategory={currentCategory}
                                currentCategoryName={currentCategoryName}
                                missedQuestions={missedQuestions}
                                selectedAnswers={selectedAnswers}
                                onChangeOption={this.onChangeOption}
                                containerRef={this.containerFaqViewRef}
                            />
                            <BoxActionsStyled>{this.renderButtons()}</BoxActionsStyled>
                        </Col>
                    </Row>
                </ContentStyled>
            </AssessmentToolStyled>
        );
    }
}

AssessmentTool.propTypes = {
    closeModal: func.isRequired,
    fetchProspectQuestionnaire: func.isRequired,
    history: HISTORY.isRequired,
    isLoading: func.isRequired,
    match: MATCH.isRequired,
    openModal: func.isRequired,
    prospectSendResponses: func.isRequired,
    sendPublicPendoEvent: func.isRequired,
};

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    fetchBranding: brandingActions.fetchBranding,
    fetchProspectQuestionnaire: hrFitnessTestActions.fetchProspectQuestionnaire,
    isLoading: uiActions.isLoading,
    openModal: uiActions.openModal,
    prospectSendResponses: hrFitnessTestActions.prospectSendResponses,
    sendPublicPendoEvent: pendoActions.sendPublicPendoEvent,
};

export { AssessmentTool as AssessmentToolUnwrapped };
export default connect(null, mapDispatchToProps)(AssessmentTool);
